import React, { useCallback, useContext, useEffect, useMemo, useState } from "react";
import {
  Container,
  Grid,
  Header,
  SegmentedControl,
  SpaceBetween,
} from "@amzn/awsui-components-react";
import EventsVersionSelector from "./EventsVersionSelector";
import { OrderIdContext, StageContext } from "../../contexes/OrderIdContext";
import { useMarketplaceId } from "../../contexes/MarketplaceIdContext";
import useFetchDetails from "../../hooks/useFetchDetails";
import {
  getCbeLogGroups,
  getEventDateRange,
  getEventLogGroups,
} from "../../helpers/logUtils";
import {
  getAllProductIds,
  isBundleTransaction,
  isBundleNonSplitTransaction,
} from "../../helpers/bundleUtils";
import CbeVersionSelector from "../cbe/CbeVersionSelector";
import EventButtons from "../common/EventButtons";
import ErrorAlert from "../common/ErrorAlert";
import { useGlProduct } from "../../contexes/GlProductContext";
import { useFlowType } from "../../hooks/useFlowType";
import {
  FLOW_TYPES,
  FlowType,
  getFlowSelectorLabel,
} from "../../helpers/flowTypeUtils";
import { isDigitalBusinessDocument } from "../../helpers/typeChecks";

export default function Results() {
  const orderId = useContext(OrderIdContext);
  const stage = useContext(StageContext);
  const { marketplaceId } = useMarketplaceId();
  const { glProduct } = useGlProduct();

  const [productsToggleConfig, setProductsToggleConfig] = useState<{
    show: boolean;
    onToggle: () => void;
  } | null>(null);

  const { data: detailsData } = useFetchDetails(stage, orderId);

  const eventData = detailsData?.events || [];
  const cbeData = detailsData?.cbes || [];

  const { allProductIds, isBundle } = useMemo(() => {
    const documents = eventData.filter(isDigitalBusinessDocument);

    if (documents.length === 0) {
      return { allProductIds: [], isBundle: false };
    }

    const isBundleDetected = isBundleTransaction(documents[0]);
    const productIds = Array.from(getAllProductIds(documents));
    const isBundleNonSplitDetected = isBundleNonSplitTransaction(documents);

    return {
      allProductIds: productIds,
      isBundle: isBundleDetected && !isBundleNonSplitDetected,
    };
  }, [eventData]);

  const isEventNotEmpty = eventData.length > 0;
  const isCbeNotEmpty = cbeData.length > 0;

  const handleProductsToggle = useCallback((toggleFunction: () => void) => {
    setProductsToggleConfig({
      show: true,
      onToggle: toggleFunction,
    });
  }, []);

  const {
    flowType,
    setFlowType,
    showFlowTypeSelector,
    filteredEvents,
    filteredCbes,
  } = useFlowType({
    eventData,
    cbeData,
    glProduct,
  });
  const [selectedEventIndex, setSelectedEventIndex] = useState(() => {
    return filteredEvents.length > 0 ? filteredEvents.length - 1 : 0;
  });

  const [selectedCbeIndex, setSelectedCbeIndex] = useState(() => {
    return filteredCbes.length > 0 ? filteredCbes.length - 1 : 0;
  });

  useEffect(() => {
    if (
      filteredEvents.length > 0 &&
      selectedEventIndex >= filteredEvents.length
    ) {
      setSelectedEventIndex(filteredEvents.length - 1);
    }
  }, [filteredEvents, selectedEventIndex]);

  useEffect(() => {
    if (filteredCbes.length > 0 && selectedCbeIndex >= filteredCbes.length) {
      setSelectedCbeIndex(filteredCbes.length - 1);
    }
  }, [filteredCbes, selectedCbeIndex]);

  const eventLogGroups = useMemo(() => {
    if (!isEventNotEmpty) return [];

    const dateRange = getEventDateRange(
      filteredEvents[selectedEventIndex] || eventData[selectedEventIndex],
      false
    );
    const startTime = dateRange[0];

    return getEventLogGroups(stage, filteredEvents, flowType, startTime);
  }, [
    isEventNotEmpty,
    stage,
    filteredEvents,
    flowType,
    selectedEventIndex,
    eventData,
  ]);

  const cbeLogGroups = useMemo(() => {
    return getCbeLogGroups(marketplaceId, stage);
  }, [marketplaceId, stage]);

  return (
    <Grid
      gridDefinition={[
        { colspan: { default: 12, s: 6 } },
        { colspan: { default: 12, s: 6 } },
      ]}
    >
      <Container
        header={
          <Header
            variant="h2"
            actions={
              <SpaceBetween direction="horizontal" size="xs">
                <EventButtons
                  identifier={orderId}
                  stage={stage}
                  logGroups={eventLogGroups}
                  dateRange={getEventDateRange(
                    filteredEvents[selectedEventIndex] ||
                      eventData[selectedEventIndex],
                    false
                  )}
                  data={filteredEvents}
                />

                {showFlowTypeSelector && (
                  <SegmentedControl
                    selectedId={flowType}
                    onChange={({ detail }) =>
                      setFlowType(detail.selectedId as FlowType)
                    }
                    options={[
                      {
                        text: getFlowSelectorLabel(
                          FLOW_TYPES.ACCOUNTING,
                          eventData,
                          cbeData
                        ),
                        id: FLOW_TYPES.ACCOUNTING,
                      },
                      {
                        text: getFlowSelectorLabel(
                          FLOW_TYPES.DOCUMENT,
                          eventData,
                          cbeData
                        ),
                        id: FLOW_TYPES.DOCUMENT,
                      },
                    ]}
                  />
                )}
              </SpaceBetween>
            }
          >
            Events
          </Header>
        }
      >
        {isEventNotEmpty && filteredEvents.length > 0 ? (
          <EventsVersionSelector
            eventData={filteredEvents}
            onVersionChange={setSelectedEventIndex}
          />
        ) : isEventNotEmpty ? (
          <ErrorAlert
            header={
              showFlowTypeSelector
                ? `No ${
                    flowType === FLOW_TYPES.DOCUMENT ? "royalty" : "revenue"
                  } events found`
                : "No events found"
            }
            content={
              showFlowTypeSelector
                ? `Try changing the flow type filter to view ${
                    flowType === FLOW_TYPES.DOCUMENT ? "revenue" : "royalty"
                  } events.`
                : ""
            }
          />
        ) : (
          <ErrorAlert header="Could not find any events" content="" />
        )}
      </Container>
      <Container
        header={
          <Header
            variant="h2"
            actions={
              <EventButtons
                identifier={orderId}
                stage={stage}
                logGroups={cbeLogGroups}
                dateRange={getEventDateRange(
                  filteredCbes[selectedCbeIndex] || cbeData[selectedCbeIndex],
                  true
                )}
                data={filteredCbes}
                collapseProductsConfig={productsToggleConfig || undefined}
              />
            }
          >
            CBEs
          </Header>
        }
      >
        {isCbeNotEmpty && filteredCbes.length > 0 ? (
          <CbeVersionSelector
            cbeData={filteredCbes}
            onVersionChange={setSelectedCbeIndex}
            allProductIds={allProductIds}
            isBundle={isBundle}
            onProductsToggle={handleProductsToggle}
          />
        ) : isCbeNotEmpty ? (
          <ErrorAlert
            header={
              showFlowTypeSelector
                ? `No ${
                    flowType === FLOW_TYPES.DOCUMENT ? "royalty" : "revenue"
                  } CBEs found`
                : "No CBEs found"
            }
            content={
              showFlowTypeSelector
                ? `Try changing the flow type filter to view ${
                    flowType === FLOW_TYPES.DOCUMENT ? "revenue" : "royalty"
                  } CBEs.`
                : ""
            }
          />
        ) : (
          <ErrorAlert header="Could not find any CBEs" content="" />
        )}
      </Container>
    </Grid>
  );
}
